import React from 'react';

import AttachFileIcon from '@mui/icons-material/AttachFile';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import EmailIcon from '@mui/icons-material/Email';
import ImageIcon from '@mui/icons-material/Image';
import {
  Box,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import CaseEmptyRow from './CaseEmptyRow';
import CaseHistoryCard from './CaseHistoryCard';
import CaseRelatedCard from './CaseRelatedCard';
import { SampleRelatedRecords } from './types';

interface Props {
  caseNumber: string;
  sample: SampleRelatedRecords;
}

const headerCellSx = { fontWeight: 'bold', whiteSpace: 'nowrap' } as const;

export default function CaseRelatedTab({ caseNumber, sample }: Props) {
  const { emails, attachments } = sample;

  return (
    <Stack spacing={1.5}>
      <CaseRelatedCard
        icon={<EmailIcon fontSize="small" />}
        iconColor="primary.main"
        title={`Emails (${emails.length})`}
        hasViewAll={emails.length > 0}
      >
        <Table size="small" aria-label="Case emails">
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>Subject</TableCell>
              <TableCell sx={headerCellSx}>From Address</TableCell>
              <TableCell sx={headerCellSx}>To Address</TableCell>
              <TableCell sx={headerCellSx}>Message Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {emails.length === 0 && <CaseEmptyRow colSpan={4} message="No emails yet." />}
            {emails.map((email) => (
              <TableRow key={`${email.date}-${email.subject}`}>
                <TableCell>
                  <MuiLink component="span" underline="hover">
                    {email.subject}
                  </MuiLink>
                </TableCell>
                <TableCell>
                  <MuiLink href={`mailto:${email.from}`} underline="hover">
                    {email.from}
                  </MuiLink>
                </TableCell>
                <TableCell>{email.to}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{email.date}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CaseRelatedCard>

      <CaseRelatedCard
        icon={<ChatBubbleIcon fontSize="small" />}
        iconColor="success.main"
        title="Case Comments (0)"
        actionLabel="New"
      />

      <CaseHistoryCard caseNumber={caseNumber} />

      <CaseRelatedCard
        icon={<AttachFileIcon fontSize="small" />}
        iconColor="text.secondary"
        title={`Attachments (${attachments.length})`}
        actionLabel="Upload Files"
        hasViewAll={attachments.length > 0}
      >
        {attachments.length === 0 && (
          <Typography color="text.secondary" sx={{ p: 2 }}>
            No attachments yet.
          </Typography>
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, p: 2 }}>
          {attachments.map((file) => (
            <Stack key={file.name} direction="row" spacing={1} alignItems="flex-start">
              <ImageIcon fontSize="small" color="action" />
              <Box>
                <MuiLink component="span" variant="body2" underline="hover">
                  {file.name}
                </MuiLink>
                <Typography variant="caption" color="text.secondary" display="block">
                  {file.date} · Attachment
                </Typography>
              </Box>
            </Stack>
          ))}
        </Box>
      </CaseRelatedCard>
    </Stack>
  );
}
